import {
    accessTicketIsCurrent,
    observeAccessVersion,
    rejectResourceAccess,
    type AccessTicket,
} from "./resourceAccess";

/** The response raised its project's policy past the version its request was admitted under:
 * someone changed access, or the request's own write did (a draft leaving draft changes who may
 * read it). The body is fenced like any other; a command the server keys by its request id can be
 * sent again once the project is admitted (`replayAfterPolicyRaise`). */
export class PolicyRaisedError extends Error {
    constructor() {
        super("Project access changed during this request");
    }
}

/** One response contract for Axios, Fetch and streaming/download transports.
 * Only the identity that started a request may observe its authority headers.
 * A newer policy fences the body, including an otherwise successful response.
 */
export function observeResponseAdmission(
    ticket: AccessTicket,
    header: (name: string) => unknown,
    status: number,
    admissionRequest = false,
): void {
    if (!accessTicketIsCurrent(ticket)) throw new Error("A stale project response was discarded");
    if (
        header("x-access-admission") === "denied" ||
        status === 401 ||
        (admissionRequest && status === 403)
    ) {
        rejectResourceAccess();
        throw new Error("Project access changed during this request");
    }
    // The single admission owner validates and installs the complete /me body
    // atomically. Observing its new version here would stale its own ticket,
    // discard the replacement lease and require a redundant second /me call.
    if (admissionRequest && status >= 200 && status < 300) return;
    const project = header("x-access-project-id");
    const rawVersion = header("x-access-policy-version");
    if (typeof project === "string" && typeof rawVersion === "string" && /^\d+$/.test(rawVersion)) {
        const version = Number(rawVersion);
        if (!Number.isSafeInteger(version)) throw new Error("Invalid project access version");
        observeAccessVersion(project, version, ticket);
    }
    if (!accessTicketIsCurrent(ticket)) throw new PolicyRaisedError();
}
